import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBookById, createBook, updateBook, fetchCategories } from '../../../components/API/Store/bookAPI';
import { fetchFulfillmentPartners } from '../../../components/API/Store/fulfillmentPartnerAPI'; // Import the API call for partners
import './AddEditBook.css';

const robustDecode = (url) => {
    if (!url) return '';
    let decodedUrl = url;
    const iterations = 5;

    for (let i = 0; i < iterations; i++) {
        let previousUrl = decodedUrl;
        decodedUrl = decodedUrl
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#039;/g, "'")
            .replace(/&#x2F;/g, '/')
            .replace(/&#x3D;/g, '=')
            .replace(/\+/g, ' ');

        try {
            decodedUrl = decodeURIComponent(decodedUrl);
        } catch (e) {
            console.warn('Decoding stopped due to error:', e);
            return decodedUrl;
        }

        if (previousUrl === decodedUrl) break;
    }

    return decodedUrl;
};

const AddEditBook = ({ id, onSave }) => {
    const [book, setBook] = useState({
        title: '',
        author: '',
        price: '',
        cover_image: '',
        stock: '',
        category_id: '',
        isbn: '',
        publication_date: '',
        description: '',
        partner_id: '' // Updated field to store the partner's ID
    });
    const [decodedCoverImage, setDecodedCoverImage] = useState('');
    const [categories, setCategories] = useState([]);
    const [partners, setPartners] = useState([]); // State to hold the list of partners
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        const initializeForm = async () => {
            setLoading(true);
            setError('');
            try {
                await loadCategories();
                await loadPartners(); // Fetch the list of partners
                if (id) {
                    await fetchBook(id);
                }
            } catch (err) {
                setError('Failed to initialize form. Please try again.');
            }
            setLoading(false);
        };

        initializeForm();
    }, [id]);

    const loadCategories = async () => {
        const { success, categories, message } = await fetchCategories();
        if (success) {
            setCategories(categories);
        } else {
            throw new Error(message);
        }
    };

    const loadPartners = async () => {
        const { success, partners, message } = await fetchFulfillmentPartners();
        if (success) {
            setPartners(partners);
        } else {
            throw new Error(message);
        }
    };

    const fetchBook = async (bookId) => {
        const { success, book, message } = await fetchBookById(bookId);
        if (success) {
            const processedBook = Object.fromEntries(
                Object.entries(book).map(([key, value]) => [key, value != null ? String(value) : ''])
            );
            setBook(processedBook);
            if (processedBook.cover_image) {
                setDecodedCoverImage(robustDecode(processedBook.cover_image));
            }
        } else {
            throw new Error(message);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'cover_image') {
            setDecodedCoverImage(value);
            setBook(prevBook => ({
                ...prevBook,
                [name]: value // Store the unmodified URL
            }));
        } else {
            setBook(prevBook => ({
                ...prevBook,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const bookToSubmit = Object.fromEntries(
                Object.entries(book).filter(([_, value]) => value !== '')
            );

            const { success, message } = id
                ? await updateBook(id, bookToSubmit)
                : await createBook(bookToSubmit);

            if (success) {
                onSave(); // Notify AdminDashboard that a book has been added/updated
            } else {
                setError(`Failed to ${id ? 'update' : 'create'} book. ${message}`);
            }
        } catch (err) {
            setError(`Failed to ${id ? 'update' : 'create'} book. ${err.message}`);
        }

        setLoading(false);
    };

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="add-edit-book-container">
            <form onSubmit={handleSubmit} className="add-edit-book">
                <h2>{id ? 'Edit' : 'Add'} Book</h2>
                {error && <div className="error-message">{error}</div>}
                <div className="form-group">
                    <label htmlFor="title">Title</label>
                    <input type="text" id="title" name="title" value={book.title} onChange={handleChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="author">Author</label>
                    <input type="text" id="author" name="author" value={book.author} onChange={handleChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="price">Price</label>
                    <input type="number" id="price" name="price" value={book.price} onChange={handleChange} required step="0.01" min="0" />
                </div>

                <div className="form-group">
                    <label htmlFor="cover_image">Cover Image URL</label>
                    <input type="text" id="cover_image" name="cover_image" value={decodedCoverImage} onChange={handleChange} />
                </div>

                <div className="form-group">
                    <label htmlFor="stock">Stock</label>
                    <input type="number" id="stock" name="stock" value={book.stock} onChange={handleChange} required min="0" />
                </div>

                <div className="form-group">
                    <label htmlFor="isbn">ISBN</label>
                    <input type="text" id="isbn" name="isbn" value={book.isbn} onChange={handleChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="publication_date">Publication Date</label>
                    <input type="date" id="publication_date" name="publication_date" value={book.publication_date} onChange={handleChange} required />
                </div>

                <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        value={book.description}
                        onChange={handleChange}
                        rows="5"
                        placeholder="Enter book description here..."
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="category_id">Category</label>
                    <select id="category_id" name="category_id" value={book.category_id} onChange={handleChange} required>
                        <option value="">Select Category</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="partner_id">Partner</label>
                    <select id="partner_id" name="partner_id" value={book.partner_id} onChange={handleChange} required>
                        <option value="">Select Partner</option>
                        {partners.map(partner => (
                            <option key={partner.id} value={partner.id}>{partner.name}</option>
                        ))}
                    </select>
                </div>

                <button type="submit" disabled={loading}>
                    {loading ? 'Processing...' : (id ? 'Update' : 'Add') + ' Book'}
                </button>
            </form>
        </div>
    );
};

export default AddEditBook;
